import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { whatsAppService } from "../../Api/Services/whatsAppService";
import { WhatsAppTemplate } from "../../Models/WhatsApp";
import { getWhatsAppTemplateTypeLabel } from "../../Utils/Enums";
import Button from "../Button/Button";

const META_STATUS_LABELS: Record<string, { label: string; className: string }> = {
  APPROVED: { label: "Aprovado pela Meta", className: "bg-green-100 text-green-800" },
  PENDING: { label: "Em análise pela Meta", className: "bg-yellow-100 text-yellow-800" },
  REJECTED: { label: "Recusado pela Meta", className: "bg-red-100 text-red-800" },
  ERROR: { label: "Erro ao enviar à Meta", className: "bg-red-100 text-red-800" }
};

type WhatsAppTemplateCardProps = {
  template: WhatsAppTemplate;
  showMetaStatus: boolean;
  onSaved: (template: WhatsAppTemplate) => void;
};

const WhatsAppTemplateCard = ({ template, showMetaStatus, onSaved }: WhatsAppTemplateCardProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [body, setBody] = useState(template.body);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setBody(template.body);
  }, [template.body]);

  const isDirty = body.trim() !== template.body;
  const metaStatus = template.metaTemplateStatus ? META_STATUS_LABELS[template.metaTemplateStatus] : undefined;

  // Inserts at the cursor so the store can place a placeholder mid-sentence
  const insertPlaceholder = (placeholder: string) => {
    const token = `[${placeholder}]`;
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? body.length;
    const end = textarea?.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);

    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = async () => {
    if (!body.trim()) {
      toast.error("A mensagem não pode ficar vazia.");
      return;
    }

    try {
      setIsSaving(true);
      const saved = await whatsAppService.updateTemplate(template.type, body);
      onSaved(saved);
      toast.success("Mensagem salva!");
    } catch (error: any) {
      const message = typeof error?.response?.data === "string" ? error.response.data : undefined;
      toast.error(message ?? "Não foi possível salvar a mensagem.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      const saved = await whatsAppService.resetTemplate(template.type);
      onSaved(saved);
      toast.success("Mensagem padrão restaurada!");
    } catch {
      toast.error("Não foi possível restaurar a mensagem padrão.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border bg-white rounded shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-primary-120">
        <h3 className="font-semibold">{getWhatsAppTemplateTypeLabel(template.type)}</h3>
        <span className={`text-xs rounded-full px-2 py-0.5 ${template.isDefault ? "bg-white text-gray-600" : "bg-primary-50 text-primary-700"}`}>
          {template.isDefault ? "Padrão" : "Personalizada"}
        </span>
        {showMetaStatus && (
          <span className={`text-xs rounded-full px-2 py-0.5 ${metaStatus?.className ?? "bg-white text-gray-600"}`}>
            {metaStatus?.label ?? "Não enviado à Meta"}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3">
        {showMetaStatus && template.metaTemplateError && (
          <p className="text-xs text-red-700">{template.metaTemplateError}</p>
        )}

        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          maxLength={1024}
          className="w-full border border-gray-300 rounded p-3 text-sm hover:border-gray-400 focus:outline-none focus:border-primary-600"
        />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">{"Inserir:"}</span>
          {template.placeholders.map((placeholder) => (
            <button
              key={placeholder}
              type="button"
              onClick={() => insertPlaceholder(placeholder)}
              className="text-xs border border-gray-300 rounded-full px-2 py-0.5 text-gray-700 hover:bg-primary-50 hover:border-primary-600"
            >
              {`[${placeholder}]`}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {!template.isDefault && (
            <button
              type="button"
              onClick={() => void handleReset()}
              disabled={isSaving}
              className="text-sm text-primary-600 px-3 py-2 hover:text-primary-700 hover:underline disabled:opacity-60"
            >
              {"Restaurar padrão"}
            </button>
          )}
          <Button isLoading={isSaving}
            type="button"
            onClick={() => void handleSave()}
            disabled={isSaving || !isDirty}
            className="text-sm px-4 py-2"
          >
            {"Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplateCard;
